import { useMemo } from "react";

import type { SpanStatus } from "../../utils/calculateSpanStatus";

import { TimeMarkers } from "./TimeMarkers";
import { SpanBars } from "./SpanBars";
import { RangeSelector } from "./RangeSelector";

import { SpanNode } from "@/types/spans";

interface CompactWaterfallProps {
  spans: SpanNode[];
  spanStatuses: Map<string, SpanStatus>;
  timeRange: [number, number];
  onTimeRangeChange: (value: [number, number]) => void;
  calculateWaterfallPosition: (
    spanNode: SpanNode,
    traceStartTime: number,
    traceDuration: number,
  ) => { left: string; width: string; isRunning: boolean };
  onSpanClick: (span: SpanNode) => void;
}

export const CompactWaterfall = ({
  spans,
  spanStatuses,
  timeRange,
  onTimeRangeChange,
  calculateWaterfallPosition,
  onSpanClick,
}: CompactWaterfallProps) => {
  const hasSpans = spans && spans.length > 0;

  const actualStartTime = useMemo(() => {
    if (!hasSpans) {
      return 0;
    }
    return Math.min(...spans.map(span => new Date(span.start).getTime()));
  }, [spans, hasSpans]);

  const actualEndTime = useMemo(() => {
    if (!hasSpans) {
      return 0;
    }
    return Math.max(
      ...spans.filter(span => span.end !== null).map(span => new Date(span.end!).getTime()),
    );
  }, [spans, hasSpans]);

  const actualDuration = actualEndTime - actualStartTime;

  const calculateRowsNeeded = (spansToCheck: SpanNode[]): number => {
    if (spansToCheck.length === 0) {
      return 1;
    }

    const rowOccupancies: { start: number; end: number }[][] = [];
    const sortedSpans = [...spansToCheck].sort((a, b) => {
      const aStart = new Date(a.start).getTime();
      const bStart = new Date(b.start).getTime();
      return aStart - bStart;
    });

    sortedSpans.forEach(span => {
      const spanStart = new Date(span.start).getTime();
      const spanEnd = span.end ? new Date(span.end).getTime() : Date.now();

      let assignedRow = -1;
      for (let rowIndex = 0; rowIndex < 20; rowIndex++) {
        if (!rowOccupancies[rowIndex]) {
          rowOccupancies[rowIndex] = [];
        }

        const overlaps = rowOccupancies[rowIndex].some(
          occupied => spanStart < occupied.end && spanEnd > occupied.start,
        );

        if (!overlaps) {
          assignedRow = rowIndex;
          rowOccupancies[rowIndex].push({ start: spanStart, end: spanEnd });
          break;
        }
      }

      if (assignedRow === -1) {
        const lastRow = rowOccupancies.length;
        rowOccupancies[lastRow] = [{ start: spanStart, end: spanEnd }];
      }
    });

    return Math.max(1, Math.min(rowOccupancies.length, 10));
  };

  const rows = hasSpans ? calculateRowsNeeded(spans) : 1;
  const rowHeight = 6;

  return (
    <div className="relative w-full border-b border-gray-200 bg-gray-50 px-4 py-4">
      <div
        className="relative min-h-[54px] w-full"
        style={{ height: `${rows * rowHeight + 30}px` }}
      >
        {actualDuration > 0 && <TimeMarkers traceDuration={actualDuration} markerCount={5} />}
        {hasSpans ? (
          <SpanBars
            spans={spans}
            spanStatuses={spanStatuses}
            traceStartTime={actualStartTime}
            traceDuration={actualDuration}
            maxRows={rows}
            rowHeight={rowHeight}
            calculateWaterfallPosition={calculateWaterfallPosition}
            onSpanClick={onSpanClick}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-xs text-gray-500">No spans available</div>
          </div>
        )}
        {actualDuration > 0 && (
          <RangeSelector timeRange={timeRange} onValueChange={onTimeRangeChange} />
        )}
      </div>
    </div>
  );
};
